import { downloadFile } from '@app/hooks/useDownload';
import { useNavigation } from '@app/hooks/useNavigation';
import { useQueryParams } from '@app/hooks/useQuestionaireRoute';
import { CompletedSignatureToken, parseJwt } from '@app/utils/parseJwt';
import LangSelect from '@components/block/Header/LangSelect';
import { Logo } from '@components/block/Header/Logo';
import { Button } from '@components/ui';
import { useGetShareTokenJWTQuery } from '@dieterApi/sharetoken/useGetShareTokenJWTQuery';
import { useDownloadSignedDocumentMutation } from '@dieterApi/signature/useDownloadSignedDocumentMutation';
import { Alert, CircularProgress } from '@mui/material';
import { Download, FileCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import './documentshare.sass';

export default function SignedDocumentDownload() {
  const query = useQueryParams();
  const { navigation, setNavigation } = useNavigation();
  const token = query.get('token') || '';
  const { data: shareTokenData, loading: tokenLoading } = useGetShareTokenJWTQuery({
    variables: { id: token },
  });
  const jwt = shareTokenData?.getShareTokenJWT?.jwt;
  const tokenInfo = jwt ? parseJwt<CompletedSignatureToken>(jwt) : undefined;
  const [downloadSignedDocument, { loading }] = useDownloadSignedDocumentMutation();
  const [error, setError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState(false);

  useEffect(() => {
    if (navigation.isPrintView) return;
    setNavigation((nav) => void (nav.isPrintView = true));
  }, [navigation]);

  useEffect(() => {
    return () => {
      setNavigation((nav) => void (nav.isPrintView = false));
    };
  }, []);

  const handleDownload = async () => {
    setError(null);
    try {
      const result = await downloadSignedDocument({ variables: { token: jwt! } });
      const data = result?.data?.downloadSignedDocument;
      if (data) {
        downloadFile(data);
        setDownloaded(true);
      }
    } catch (err: any) {
      console.error('Error downloading signed document:', err);
      setError(err?.message || 'Fehler beim Herunterladen des Dokuments.');
    }
  };

  if (!token || (!tokenLoading && !tokenInfo)) {
    return (
      <div className="dtDocumentShare flex flex-col gap-[10px] h-full!">
        <div className="fixed top-6 right-6">
          <LangSelect inHeader={false} />
        </div>
        <div className="header flex gap-[10px]">
          <h1 className="text-3xl font-medium">Unterschriebenes Dokument</h1>
          <Logo clickable={false} variant="light" />
        </div>
        <p className="text-red-600 font-semibold bg-red-100 p-4 rounded">
          Der Link ist ungültig oder abgelaufen. Bitte überprüfe den Link aus deiner E-Mail.
        </p>
      </div>
    );
  }

  if (tokenLoading) {
    return (
      <div className="dtDocumentShare flex flex-col gap-[10px] h-full!">
        <div className="fixed top-6 right-6">
          <LangSelect inHeader={false} />
        </div>
        <div className="header flex gap-[10px]">
          <h1 className="text-3xl font-medium">Unterschriebenes Dokument</h1>
          <Logo clickable={false} variant="light" />
        </div>
        <div className="flex items-center justify-center mt-12">
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="dtDocumentShare flex flex-col gap-[10px] h-full!">
      <div className="fixed top-6 right-6">
        <LangSelect inHeader={false} />
      </div>
      <div className="header flex gap-[10px]">
        <h1 className="text-3xl font-medium">Unterschriebenes Dokument</h1>
        <Logo clickable={false} variant="light" />
      </div>

      <div className="flex flex-col gap-6 max-w-[600px]">
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-4">
          <FileCheck className="text-green-600" size={28} />
          <div>
            <p className="font-semibold text-green-800">Vertrag vollständig unterschrieben</p>
            <p className="text-sm text-green-700">
              Beide Parteien haben den Vertrag unterzeichnet. Du kannst das unterschriebene Dokument jetzt
              herunterladen.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 flex flex-col gap-2">
          <div className="flex justify-between">
            <span className="text-gray-500">Dokument:</span>
            <span className="font-medium">{tokenInfo.documentLabel}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Unterzeichner 1:</span>
            <span className="font-medium">{tokenInfo.signee1Name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Unterzeichner 2:</span>
            <span className="font-medium">{tokenInfo.signee2Name}</span>
          </div>
        </div>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {downloaded && <Alert severity="success">Dokument wurde erfolgreich heruntergeladen.</Alert>}

        <Button onClick={handleDownload} disabled={loading} data-testid="button-download-signed">
          {loading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <CircularProgress size={16} color="inherit" />
              Wird generiert...
            </span>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Download size={16} />
              Unterschriebenes Dokument herunterladen
            </span>
          )}
        </Button>

        <p className="text-xs text-gray-400 text-center">
          Dieser Link ist 30 Tage gültig. Bitte lade das Dokument rechtzeitig herunter.
        </p>
      </div>
    </div>
  );
}
